import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, ClipboardList, KeyRound } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReceptionistLogin() {
  const nav = useNavigate();
  const { setSession } = useAuth();
  const [customId, setCustomId] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/auth/receptionist/login`, {
        custom_id: customId, password,
      });
      // Reuse the shared AuthContext so token + user are stored with the
      // same convention as admin / doctor logins.
      setSession({
        token: data.token,
        user: {
          id: data.receptionist_id,
          role: "receptionist",
          custom_id: data.custom_id,
          full_name: data.full_name,
          hospital_id: data.hospital_id,
          permissions: data.permissions,
        },
      });
      toast.success(`Welcome, ${data.full_name}`);
      nav("/receptionist/dashboard");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 403) {
        toast.error(typeof detail === "string" ? detail : "Account inactive");
      } else if (err.response?.status === 429) {
        toast.error("Too many attempts. Please try again in a few minutes.");
      } else {
        toast.error("Invalid credentials");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="receptionist-login-page">
      <header className="p-4">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900">
          <ArrowLeft className="w-4 h-4" /> Back to Home
        </Link>
      </header>
      <main className="flex-1 flex items-center justify-center px-4">
        <Card className="w-full max-w-md p-8 space-y-5">
          <div className="text-center space-y-2">
            <div className="mx-auto w-12 h-12 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center">
              <ClipboardList size={22} />
            </div>
            <h1 className="font-heading text-2xl font-semibold text-slate-900">Receptionist Login</h1>
            <p className="text-xs text-slate-500">Front-desk staff sign-in</p>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label>Custom ID</Label>
              <Input value={customId} onChange={(e) => setCustomId(e.target.value.trim())}
                     required minLength={4} maxLength={32}
                     placeholder="e.g. rec_priya01"
                     data-testid="receptionist-login-custom-id" />
            </div>
            <div>
              <Label>Password</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                     required minLength={8} data-testid="receptionist-login-password" />
            </div>
            <Button type="submit" disabled={submitting}
                    className="w-full bg-indigo-500 hover:bg-indigo-600 text-white"
                    data-testid="receptionist-login-submit">
              {submitting ? "Signing in…" : (
                <><KeyRound size={16} className="mr-1" /> Sign In</>
              )}
            </Button>
          </form>

          <p className="text-[11px] text-slate-500 text-center">
            Access limited to features enabled by your hospital administrator.
          </p>
        </Card>
      </main>
    </div>
  );
}
